import React, { useCallback, useMemo, useState } from "react";
import { useSpring, animated } from "@react-spring/web";
import Image from "next/image";
import { Frame } from "@shopify/polaris";
import { CancelIcon } from "@novel/shared/icons";
import Bg from "@customer-gallery-public/bg.jpg";
import LeftNavigationPane from "./LeftNavigationPane";
import { ClaimedModal } from "@customer-gallery/components/GalleryPageCmp/ClaimedModal";
import { useNovelCustomerDispatch, useNovelCustomerSelector } from "@customer-gallery/redux/reduxHooks";
import { ICryptoAddressWithTokensAndWallets } from "@novel/shared/interfaces/CryptoAddress";
import { INovelUser } from "@novel/shared/interfaces/NovelUser";
import capitalize from "lodash/capitalize";
import { ITokenWithMetadataAndCollection } from "@novel/shared/interfaces/Token";
import Head from "next/head";
import { shortenCryptoAddress } from "@novel/shared/utils/shortenCryptoAddress";
interface Props {
  readonly children?: React.ReactNode;
  readonly addresses: ICryptoAddressWithTokensAndWallets[];
  readonly user?: INovelUser | null;
  readonly customerId?: string;
}
const GalleryWrapper = ({
  children,
  addresses,
  user,
  customerId
}: Props): JSX.Element => {
  const mobileNavigationActive = useNovelCustomerSelector(state => state.auth.openSidebar);
  const dispatch = useNovelCustomerDispatch();
  const [claimedToken, setClaimedToken] = useState<null | ITokenWithMetadataAndCollection>();
  const closeClaimedModal = useCallback(() => {
    setClaimedToken(null);
  }, []);
  const onProfilePicClaimedClick = useCallback((claimedToken: undefined | ITokenWithMetadataAndCollection) => {
    if (claimedToken) {
      setClaimedToken(claimedToken);
    }
  }, []);
  const styleProps = useSpring({
    from: {
      left: "-100%"
    },
    left: mobileNavigationActive ? "0" : "-100%"
  });
  const onNavigationToggle = useCallback(() => {
    dispatch({
      type: "CHANGE_SIDEBAR_STATUS",
      payload: {
        open: false
      }
    });
  }, [dispatch]);
  const firstCryptoAddress = useMemo(() => user?.cryptoAddresses?.[0]?.address ? shortenCryptoAddress(user?.cryptoAddresses?.[0]?.address) : "", [user?.cryptoAddresses]);
  const userDisplayName = useMemo(() => {
    const displayName = user?.displayName?.trim() || "";
    const capitalizedName = [capitalize(user?.firstName || ""), capitalize(user?.lastName || "")].map(v => v.trim()).filter(Boolean).join(" ");
    const primaryEmail = user?.emailPublic && user?.primaryEmail?.trim() || "";
    return displayName || capitalizedName || primaryEmail || firstCryptoAddress || "";
  }, [user?.firstName, user?.lastName, user?.emailPublic, user?.primaryEmail, user?.displayName, firstCryptoAddress]);
  return <React.Fragment>
            {customerId ? <Head>
                    {userDisplayName && <title>{`${userDisplayName} | Novel`}</title>}

                    <meta name="twitter:title" content={`${userDisplayName || "Anonymous Gallery"} | Novel`} />
                    <meta name="twitter:description" content={`Check out ${userDisplayName}'s cross-chain NFT collection on Novel!`} />
                    <meta name="twitter:card" content={user?.profileImage?.image ? "summary" : "summary_large_image"} />
                    <meta name="twitter:image" content={user?.profileImage?.image || "https://cdn.jsdelivr.net/gh/uzairbangee/static-image/novel.jpg"} />

                    <meta name="og:title" content={`${userDisplayName || "Anonymous Gallery"} | Novel`} />
                    <meta name="og:description" content={`Check out ${userDisplayName}'s cross-chain NFT collection on Novel!`} />
                    <meta name="og:type" content={userDisplayName ? "profile" : "website"} />
                    <meta name="og:image" content={user?.profileImage?.image || "https://cdn.jsdelivr.net/gh/uzairbangee/static-image/novel.jpg"} />

                    {userDisplayName && <meta name="profile:username" content={userDisplayName} />}
                    {user?.firstName && <meta name="profile:first_name" content={user.firstName} />}
                    {user?.lastName && <meta name="profile:last_name" content={user.lastName} />}
                </Head> : <React.Fragment>
                    {userDisplayName && <Head>
                            <title>{`${userDisplayName} | Novel`}</title>
                        </Head>}
                </React.Fragment>}

            <div className="frame-wrapper flex">
                <Frame>
                    <Image className="BgImage" src={Bg} fill priority alt="background image" />
                    <LeftNavigationPane user={user} addresses={addresses} userDisplayName={userDisplayName} customerId={customerId} onClickClaimed={onProfilePicClaimedClick} />
                    <animated.div className={`
                            left-aside
                            `} style={styleProps}>
                        <div className="flex justify-end ml-4 mr-4 z-30">
                            <div onClick={onNavigationToggle} className="cursor-pointer">
                                <CancelIcon />
                            </div>
                        </div>
                        <LeftNavigationPane user={user} addresses={addresses} mobile userDisplayName={userDisplayName} customerId={customerId} onClickClaimed={onProfilePicClaimedClick} />
                    </animated.div>
                    <div className="main-app-container-wrapper-gallery">
                        <div className="main-app-container-container">{children}</div>
                    </div>
                </Frame>
            </div>
            {claimedToken && <ClaimedModal onClose={closeClaimedModal} claimedToken={claimedToken} />}
            <style global jsx>{`
                .claiming-modal-nft-image {
                    width: 100%;
                    border-radius: 20px;
                    overflow: hidden;
                }

                .claiming-modal-nft-image > * {
                    filter: brightness(100%) !important;
                }
                .Polaris-Navigation__Badge {
                    height: 2.4rem;
                    // width: 2.4rem;
                }
                .Polaris-Badge--statusNew {
                    height: 100%;
                    width: 100%;
                }
            `}</style>
        </React.Fragment>;
};
export default GalleryWrapper;